/**
 * Le schede di listino, decise da cosa vende il cliente.
 *
 * [sc-48583 / task 48599] Fino a ieri qui c'era una scheda sola, generica e
 * senza dati, e questo file spiegava perché: era la configurazione più
 * leggera, quella che non chiedeva a chi provava il gestionale di scegliere
 * una merceologia che poteva non essere la sua.
 *
 * Era leggera, non utile. Chi vende piastrelle e arredo bagno vuole vederli
 * separati, e dalla 48594 lo dice lui stesso in configurazione: le schede sono
 * le macro che ha scelto, e la 48391 finisce qui.
 *
 * Le schede sono le macro, non le merceologie: «Arredo bagno» ne raccoglie
 * sette, e mostrarle tutte vorrebbe dire sette card a chi ne aveva chiesta
 * una. La merceologia si sceglie quando si crea l'articolo, dove serve.
 *
 * COSA RESTA DI PRIMA
 * -------------------
 * La scheda generica. Un'azienda configurata prima della 48594 non ha
 * risposto alla domanda, e a lei non deve cambiare niente: continua a vedere
 * «Listino» con dentro i suoi articoli. Il ripiego non è un caso limite, è la
 * maggioranza di oggi.
 *
 * La sezione Listini fa parte del prodotto e non si disattiva: nessuna
 * variabile d'ambiente ne cambia le schede.
 */

import {
  PRODUCT_CATEGORIES,
  itemCategoriesFor,
  productCategoriesFor,
} from "@/config/catalog";

/**
 * La scheda di chi non ha (ancora) detto cosa vende.
 *
 * Lo slug è `altro`, che è anche la merceologia dei suoi articoli: gli URL di
 * prima continuano a funzionare, e nessuno si ritrova il magazzino sparito
 * per una configurazione che non gli era mai stata chiesta.
 */
export const GENERIC_CATEGORY = Object.freeze({
  slug: "altro",
  label: "Listino",
  icon: "fa-solid fa-box",
});

/** Un'icona per macro. Chi non ce l'ha ricade sulla scatola della generica. */
const ICONS = Object.freeze({
  piastrelle: "fa-solid fa-border-all",
  legno: "fa-solid fa-tree",
  bagno: "fa-solid fa-bath",
  profili: "fa-solid fa-ruler-combined",
  infissi: "fa-solid fa-door-open",
  altro: "fa-solid fa-box",
});

/**
 * Le schede da mostrare: quelle scelte, più quelle che contengono articoli.
 *
 * La seconda metà non è un di più. Chi ha caricato articoli prima che la
 * domanda «cosa vendi» esistesse li ha tutti in «altro»: mostrargli solo le
 * schede scelte gli farebbe sparire il magazzino dai listini, senza che nessuno
 * abbia cancellato niente. Vale in avanti allo stesso modo, per gli articoli
 * che restano indietro quando si corregge la configurazione.
 *
 * Le schede in più portano `contieneArticoli`, così chi mostra l'elenco può
 * dire perché sono lì: non sono state scelte, ci sono degli articoli dentro.
 *
 * Resta tutto in parametri invece di leggere lo store: questa funzione
 * dev'essere pura e provabile da sola. Chi la chiama sa dove prendere il
 * profilo, lei no.
 *
 * @param {string[]} [chosen] macro da `companies.productCategories`
 * @param {string[]} [inUse] valori di `items.category` con articoli attivi
 * @returns {Array<{slug: string, label: string, icon: string, contieneArticoli: boolean}>}
 */
export function listiniCategories(chosen, inUse) {
  const scelte = Array.isArray(chosen) ? chosen : [];
  // Chi non ha mai risposto resta con la scheda generica, che è anche la
  // merceologia dei suoi articoli: per lui non cambia niente, ed è la
  // maggioranza di oggi.
  if (scelte.length === 0) return [GENERIC_CATEGORY];

  const usate = Array.isArray(inUse) ? inUse : [];
  const conArticoli = PRODUCT_CATEGORIES.filter((macro) =>
    macro.categories.some((categoria) => usate.includes(categoria))
  ).map((macro) => macro.value);

  const slugs = [...new Set([...scelte, ...conArticoli])];
  return productCategoriesFor(slugs).map((macro) => ({
    slug: macro.value,
    label: macro.label,
    icon: ICONS[macro.value] || GENERIC_CATEGORY.icon,
    contieneArticoli: !scelte.includes(macro.value),
  }));
}

/** Gli slug che possono comparire nell'indirizzo di una scheda. */
export const ALLOWED_CATEGORY_SLUGS = Object.freeze([
  ...PRODUCT_CATEGORIES.map((macro) => macro.value),
]);

/**
 * Vero se lo slug è una scheda che il gestionale conosce.
 *
 * Non guarda cosa ha scelto l'azienda, ed è voluto: la guardia di rotta gira
 * anche prima che il profilo sia arrivato, e legarla a un dato che può non
 * esserci ancora vorrebbe dire respingere una scheda vera su un accesso
 * appena fatto. Chi arriva a una scheda che non ha scelto trova un elenco
 * vuoto e nessun articolo da creare — l'API, che le categorie ammesse le sa,
 * rifiuta comunque di crearlo.
 *
 * @param {string} slug valore del parametro di rotta `:category`
 */
export function isAllowedCategory(slug) {
  return ALLOWED_CATEGORY_SLUGS.includes(String(slug || "").toLowerCase());
}

/**
 * Le merceologie che vivono dentro una scheda.
 *
 * È quello su cui si filtrano gli articoli e quello che la creazione propone.
 * Uno slug sconosciuto non ne ha nessuna: meglio un elenco vuoto che l'intero
 * magazzino sotto un'intestazione sbagliata.
 *
 * @param {string} slug valore del parametro di rotta `:category`
 * @returns {string[]} valori di `items.category`
 */
export function itemCategoriesOf(slug) {
  return itemCategoriesFor([String(slug || "").toLowerCase()]);
}

/**
 * Etichetta leggibile di una scheda, a partire dallo slug di rotta.
 *
 * Serve a chi mostra il percorso all'utente: `bagno` è un dettaglio tecnico,
 * l'utente deve leggere «Arredo bagno». Uno slug sconosciuto viene
 * capitalizzato invece di sparire.
 *
 * @param {string} slug valore del parametro di rotta `:category`
 * @param {string[]} [chosen] macro scelte, per riconoscere la scheda generica
 * @returns {string} stringa vuota se lo slug è assente
 */
export function categoryLabel(slug, chosen) {
  const value = String(slug || "");
  if (!value) return "";
  const scheda = listiniCategories(chosen).find(
    (c) => c.slug === value.toLowerCase()
  );
  if (scheda) return scheda.label;
  const macro = PRODUCT_CATEGORIES.find((m) => m.value === value.toLowerCase());
  if (macro) return macro.label;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** Rotte che appartengono alla sezione Listini. */
export const LISTINI_ROUTES = Object.freeze([
  "listini",
  "listini-category",
  "listini-add-item",
  "listini-edit-item",
]);

/**
 * Decide se una navigazione verso i Listini è consentita.
 *
 * Logica pura, senza dipendenze dal router né dallo store: riceve nome di
 * rotta e parametri, restituisce `null` se la navigazione può proseguire,
 * oppure il nome della rotta verso cui reindirizzare.
 *
 * Un URL che punta a una scheda che non esiste viene riportato all'indice, non
 * alla prima scheda disponibile: spacciare una categoria per un'altra è
 * peggio che non mostrarne nessuna.
 *
 * @param {{ name?: string, params?: object }} to rotta di destinazione
 * @returns {string|null} nome della rotta di redirect, oppure null
 */
export function listiniRedirectFor(to) {
  const name = to && to.name;
  if (!LISTINI_ROUTES.includes(name)) return null;

  const category = to.params && to.params.category;
  if (category !== undefined && !isAllowedCategory(category)) {
    return "listini";
  }

  return null;
}

export default listiniCategories;
